/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { createPortal } from 'react-dom';

export interface FilterPanelShellProps {
  title: React.ReactNode;
  onClose: () => void;
  onApply: () => void;
  onClear: () => void;
  /** Number of currently active filters — forwarded for callers that embed it in `applyLabel`. */
  activeCount: number;
  /** Already-translated label for the clear button. */
  clearLabel: React.ReactNode;
  /** Already-translated label for the apply button (typically embeds `activeCount`). */
  applyLabel: React.ReactNode;
  /** Filter section content, rendered between the header and the footer. */
  children: React.ReactNode;
  /** Prefix applied to every `data-testid` (e.g. '', 'client-', 'visit-'). */
  testIdPrefix?: string;
  /** Render the backdrop+panel through a portal into `document.body` (property-management). */
  usePortal?: boolean;
  /**
   * 'nested' (property-management): the panel is a flex child of the backdrop
   * (backdrop uses `viewportOverlayStyle`-style flex layout to push it to the
   * edge). 'sibling' (client/visit): backdrop and panel are independently
   * `position: fixed`, rendered side by side in a fragment.
   */
  layout?: 'nested' | 'sibling';
  backdropStyle?: React.CSSProperties;
  panelStyle?: React.CSSProperties;
  panelClassName?: string;
  headerStyle?: React.CSSProperties;
  headerClassName?: string;
  headerTitleStyle?: React.CSSProperties;
  closeButtonStyle?: React.CSSProperties;
  closeButtonClassName?: string;
  bodyStyle?: React.CSSProperties;
  footerStyle?: React.CSSProperties;
  footerClassName?: string;
  clearButtonStyle?: React.CSSProperties;
  clearButtonClassName?: string;
  applyButtonStyle?: React.CSSProperties;
  applyButtonClassName?: string;
  closeIcon: React.ReactNode;
  /** Button/UnstyledButton component used by the caller's design system. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ButtonComponent: React.ComponentType<any>;
  /** Text/span component used by the caller's design system. */
  TextComponent: React.ComponentType<{ children: React.ReactNode; style?: React.CSSProperties; className?: string }>;
}

/**
 * Shared backdrop + aside + header + footer skeleton for the property, client
 * and visit filter panels — each caller supplies its own filter body as
 * `children`, its own design-system Button/Text components, and its own
 * already-translated labels so per-feature i18n namespaces stay untouched.
 *
 * @param {*} props - FilterPanelShellProps
 *
 * @returns {*} the backdrop+panel element, optionally portaled to `document.body`.
 */
export const FilterPanelShell: React.FC<FilterPanelShellProps> = ({
  title,
  onClose,
  onApply,
  onClear,
  clearLabel,
  applyLabel,
  children,
  testIdPrefix = '',
  usePortal = false,
  layout = 'sibling',
  backdropStyle,
  panelStyle,
  panelClassName,
  headerStyle,
  headerClassName,
  headerTitleStyle,
  closeButtonStyle,
  closeButtonClassName,
  bodyStyle,
  footerStyle,
  footerClassName,
  clearButtonStyle,
  clearButtonClassName,
  applyButtonStyle,
  applyButtonClassName,
  closeIcon,
  ButtonComponent: Button,
  TextComponent: Text,
}) => {
  const panel = (
    <aside
      data-testid={`${testIdPrefix}filter-panel`}
      className={panelClassName}
      onClick={(event) => event.stopPropagation()}
      style={panelStyle}
    >
      <header className={headerClassName} style={headerStyle}>
        <Text style={headerTitleStyle}>{title}</Text>
        <Button
          type="button"
          onClick={onClose}
          className={closeButtonClassName}
          style={closeButtonStyle}
        >
          {closeIcon}
        </Button>
      </header>

      {/*
        The scroll container, and it defaults rather than waiting to be
        configured. Every caller but one passed `flex: 1; overflow-y: auto`
        here; property-management passed no `bodyStyle` at all, so its body
        grew to its content (3013px inside a 900px panel) and the footer --
        with the Apply button on it -- was laid out below the panel and
        clipped away by the panel's own `overflow: hidden`. The panel read as
        "the filters do not scroll AND choosing one changes nothing", because
        the only control that commits a filter was off-screen with no way to
        reach it.
        `minHeight: 0` is the half the other callers were missing: without
        it a column flex child refuses to shrink below its content, so
        `flex: 1` alone does not guarantee the body ever becomes scrollable.
        Caller styles still spread last and win.
      */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', ...bodyStyle }}>
        {children}
      </div>

      <footer className={footerClassName} style={footerStyle}>
        <Button
          type="button"
          onClick={onClear}
          className={clearButtonClassName}
          style={clearButtonStyle}
        >
          {clearLabel}
        </Button>
        <Button
          type="button"
          onClick={onApply}
          className={applyButtonClassName}
          style={applyButtonStyle}
        >
          {applyLabel}
        </Button>
      </footer>
    </aside>
  );

  const content = layout === 'nested' ? (
    <div
      data-testid={`${testIdPrefix}filter-backdrop`}
      onClick={onClose}
      style={backdropStyle}
    >
      {panel}
    </div>
  ) : (
    <>
      <div
        data-testid={`${testIdPrefix}filter-backdrop`}
        onClick={onClose}
        style={backdropStyle}
      />
      {panel}
    </>
  );

  return usePortal ? createPortal(content, document.body) : content;
};

export default FilterPanelShell;
